import { isMockServices } from 'src/utils';
import mock from '../utils/mock';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';
import type {
  Coordinates,
  Table,
  TableRow,
} from 'src/services/documents/types';

export type ReconcileExtractedFieldAction = 'accept' | 'undo' | 'delete';

export interface ReconcileExternalFieldPayload {
  loanId: string;
  documentId: string;
  encompassFieldId: string;
  externalFieldId?: string;
  jsonPath?: string;
  fieldValue: string;
  historyOrder?: string;
  historyTargetField?: 'VOD' | 'VOE' | 'VOR';
}

export interface ReconcileExtractedFieldParams {
  extractedDataId: string;
  action: ReconcileExtractedFieldAction;
  taskId?: string;
  field?: {
    fieldValue?: string | null;
    tableValue?: Table | null;
    fieldConfidence?: number;
    boundingBoxes?: Coordinates;
    manuallyCorrected?: boolean;
    pageId?: string | null;
  };
  tableRow?: {
    rowId: string;
    rowIndex: number;
    row?: TableRow;
  };
  externalField?: ReconcileExternalFieldPayload;
}

export interface ReconcileExtractedFieldResponse {
  success: boolean;
  action: ReconcileExtractedFieldAction;
  extractedData?: {
    id?: string;
    fieldValue?: string | null;
    tableValue?: Table | null;
    fieldConfidence?: number;
    boundingBoxes?: Coordinates;
    manuallyCorrected?: boolean;
    userApproved?: boolean;
    pageId?: string | null;
  };
  task?: {
    id: string;
    status: string;
  } | null;
  tableRow?: {
    rowId: string;
    rowIndex: number;
    row: TableRow | null;
  };
}

const buildMockReconciliationResponse = (
  params: ReconcileExtractedFieldParams,
): ReconcileExtractedFieldResponse => {
  const isAccepted = params.action === 'accept';
  let task: ReconcileExtractedFieldResponse['task'] = null;
  let tableRow: ReconcileExtractedFieldResponse['tableRow'];

  if (params.taskId) {
    task = {
      id: params.taskId,
      status: isAccepted ? 'closed' : 'open',
    };
  }

  if (params.tableRow) {
    let row: TableRow | null = null;
    if (params.tableRow.row) {
      row = {
        ...params.tableRow.row,
        userApproved: isAccepted,
      };
    }

    tableRow = {
      rowId: params.tableRow.rowId,
      rowIndex: params.tableRow.rowIndex,
      row,
    };
  }

  return {
    success: true,
    action: params.action,
    extractedData: {
      id: params.extractedDataId,
      ...params.field,
      userApproved: isAccepted,
    },
    task,
    tableRow,
  };
};

const reconcileExtractedField = async (
  params: ReconcileExtractedFieldParams,
): Promise<ReconcileExtractedFieldResponse> => {
  if (isMockServices()) {
    return await mock(buildMockReconciliationResponse(params));
  }

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const body = {
    action: params.action,
    ...(params.taskId ? { taskId: params.taskId } : {}),
    ...(params.field ? { field: params.field } : {}),
    ...(params.tableRow ? { tableRow: params.tableRow } : {}),
    ...(params.externalField ? { externalField: params.externalField } : {}),
  };

  const response = await authTokenHttp<ReconcileExtractedFieldResponse>(
    `/extractedData/${params.extractedDataId}/reconciliation`,
    {
      method: 'POST',
      body,
    },
  );

  if (response.data) return response.data;
  throw new Error('Failed to reconcile extracted field');
};

export default reconcileExtractedField;
